import { asMaybeString } from "common/computation-model/data";
import type { WireBigNumbersComponent } from "common/generator/components/fluent-components";
import { isBound, UIBackgroundStyle } from "common/wire/types";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import React from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import "twin.macro";
import { defaultWireAppAccentContextOverlayUndo } from "webapp/lib/theme/core-themes/wireapp-theme";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import { SectionStyleProvider } from "../wire-container/wire-container";

// Zero width space to trick HTML into making space for some text
const ZeroWidthSpaceCharacterNOTEMPTY = "​";

function forceSpace(input: string | undefined): string {
    return input === undefined || input.trim() === "" ? ZeroWidthSpaceCharacterNOTEMPTY : input;
}

export const WireBigNumbers: WireRenderer<WireBigNumbersComponent> = React.memo(props => {
    const { data, title } = props;

    const theme = mergeTheme(useWireAppTheme(), [defaultWireAppAccentContextOverlayUndo]);

    return (
        <SectionStyleProvider value={UIBackgroundStyle.Highlight}>
            <TailwindThemeProvider theme={theme}>
                <div tw="py-5">
                    <h2 tw="text-2xl text-text-contextual-dark font-extrabold mb-4">{title}</h2>
                    <div
                        tw="grid gap-4 grid-template-columns[repeat(auto-fill, minmax(10.125rem, 1fr))]
                            gp-md:grid-template-columns[repeat(auto-fill, minmax(14.5rem, 1fr))]">
                        {data.map((num, index) => (
                            <div key={index} tw="text-text-dark p-5 rounded-lg shadow-md bg-bg-front overflow-hidden">
                                {isBound(num.name) && (
                                    <div tw="text-xl font-semibold truncate mb-2">
                                        {forceSpace(asMaybeString(num.name ?? ""))}
                                    </div>
                                )}
                                <div tw="text-5xl truncate">{asMaybeString(num.value?.value)}</div>
                                {isBound(num.description) && (
                                    <div tw="text-sm whitespace-pre-wrap mt-2">
                                        {asMaybeString(num.description?.value)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </TailwindThemeProvider>
        </SectionStyleProvider>
    );
});
